import { capitalize } from 'lodash';
import { useCallback } from 'react';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';
import { RangeInput } from 'shared/components/FormControl';
import { COLORS, FONT_SIZES, SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import { ColorInput } from 'dashboard/components/FormControls/FormControl';
import SelectField, { Option } from 'dashboard/components/FormControls/SelectField';
import ThemeEditorFormActionFooter, {
  ACTION_FOOTER_HEIGHT,
} from 'dashboard/components/ThemeEditorFormActionFooter';
import {
  formatter,
  withComponentWCAGWarning,
} from 'dashboard/forms/FormThemeEditorColor/FormThemeEditorColor';
import { FORM_THEME_EDITOR_ADVANCED_NAME } from 'dashboard/lib/constants/formNames';
// import { InputStyles } from 'lib/interfaces/input-styles';
import { PageTransition, TextDecorationLine } from 'lib/interfaces/ThemeSerialized';

const Title = styled('h2')`
  color: ${COLORS.black};

  padding-top: ${SPACINGS.base * 5}px;
  padding-left: ${SPACINGS.base * 2}px;
  padding-bottom: ${SPACINGS.base * 3}px;
  border-bottom: 1px solid ${COLORS.gray20};
`;
const Section = styled('div')`
  padding: ${SPACINGS.base * 2}px;
  margin-bottom: ${ACTION_FOOTER_HEIGHT};
`;
const StyledLabel = styled('label')`
  display: inline-block;
`;
const Hint = styled('p')`
  font-size: ${FONT_SIZES.small};
  color: ${COLORS.gray50};
  margin-bottom: ${SPACINGS.base}px;
`;
const StyledField = styled(Field)<any>`
  margin-bottom: ${SPACINGS.base * 3}px;
`;

interface FormThemeEditorAdvancedProps {
  defaultValues: Record<string, string>;
  disabled?: boolean;
}

const FormThemeEditorAdvanced = ({
  defaultValues,
  handleSubmit,
  disabled,
  reset,
  submitting,
  pristine,
}: FormThemeEditorAdvancedProps & InjectedFormProps) => {
  const warnForModalBackgroundColor = useCallback(
    (hexColor: string, { theme }: { theme: Record<string, string> }, _: unknown, name: string) =>
      withComponentWCAGWarning(hexColor, theme, defaultValues, name, [
        ['color-font', 'Body text color'],
        ['color-font-title', 'Heading text color'],
      ]),
    [],
  );

  return (
    <form onSubmit={handleSubmit}>
      <Title>Advanced</Title>
      <Section>
        {/* Hiding the following component temporarily as Theme set has no corresponding support */}
        {/* Will re-enable once serve side theming for attribute is implmented */}
        {/* <StyledLabel htmlFor='advanced-theme-form__input-style-select'>Input</StyledLabel>
        <Hint>Input text field style</Hint>
        <StyledField
          id='advanced-theme-form__input-style-select'
          name='theme.input-style'
          component={SelectField}
        >
          {Object.values(InputStyles).map((inputStyle) => (
            <Option key={inputStyle} value={inputStyle}>
              {capitalize(inputStyle)}
            </Option>
          ))}
        </StyledField> */}

        <StyledLabel htmlFor='advanced-theme-form__link-style-select'>Link</StyledLabel>
        <Hint>Line text decoration of links</Hint>
        <StyledField
          id='advanced-theme-form__link-style-select'
          name='theme.text-decoration-line-link'
          component={SelectField}
        >
          {Object.values(TextDecorationLine).map((textDecorationLine) => (
            <Option key={textDecorationLine} value={textDecorationLine}>
              {capitalize(textDecorationLine)}
            </Option>
          ))}
        </StyledField>

        <StyledLabel htmlFor='advanced-theme-form__background-color-input'>
          Background color
        </StyledLabel>
        <Hint>Background color for modal</Hint>
        <StyledField
          id='advanced-theme-form__background-color-input'
          warn={warnForModalBackgroundColor}
          name='theme.color-modal-background'
          component={ColorInput}
          format={formatter('color-modal-background', defaultValues)}
          defaultValue={defaultValues['color-modal-background']}
        />

        <StyledLabel htmlFor='advanced-theme-form__modal-border-radius-input'>
          Modal border radius
        </StyledLabel>
        <Hint>Border radius for modal window</Hint>
        <StyledField
          id='advanced-theme-form__modal-border-radius-input'
          name='theme.border-radius-modal'
          component={RangeInput}
          min={0}
          max={70}
          unit='px'
          format={(value: string) => (value ? value.substr(0, value.length - 2) : '')}
          normalize={(value: number) => `${value}px`}
        />

        <StyledLabel htmlFor='advanced-theme-form__page-transition-select'>
          Page transition
        </StyledLabel>
        <Hint>Animation for transitions between screens</Hint>
        <StyledField
          id='advanced-theme-form__page-transition-select'
          name='theme.page-transition'
          component={SelectField}
        >
          {Object.values(PageTransition).map((pageTransition) => (
            <Option key={pageTransition} value={pageTransition}>
              {capitalize(pageTransition)}
            </Option>
          ))}
        </StyledField>
      </Section>
      {!disabled && (
        <ThemeEditorFormActionFooter
          dataTestBlock='form-theme-editor-advanced'
          handleUndo={reset}
          submitting={submitting}
          pristine={pristine}
        />
      )}
    </form>
  );
};

export default reduxForm<{}, FormThemeEditorAdvancedProps>({
  form: FORM_THEME_EDITOR_ADVANCED_NAME,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
})(FormThemeEditorAdvanced);
